import { useCallback, useMemo, useState } from 'react';
import type { SmriqOperationExclusion, SmriqWorkItem } from '../types/smriq.types';
import { isReincludableDeclinedItem } from '../utils/smriqWorkItem';

/** The structural minimum reconcile reads: a visit id and its declined items. Both a fresh
 *  generate response and a resumed quote's persisted inclusions satisfy it. */
interface DeclinedItemsByVisit {
    id: string;
    declined_items: readonly SmriqWorkItem[];
}

// Mirrors GenerateSmriqScheduleRequest's `recommended_operation_exclusions` max:50 rule
// (smr-service). The UI must not send more than the server will accept.
export const MAX_RECOMMENDED_OPERATION_EXCLUSIONS = 50;

// Keyed by a vendor-controlled visit id, so a Map rather than an object: "__proto__"
// and "constructor" must be ordinary keys, not reads through Object.prototype.
type DeclinedByVisit = ReadonlyMap<string, ReadonlySet<string>>;

// The vendor caps the exclusions array (max:50), and one (visit, item) decline is one array
// entry, so the limit counts pairs — the same item id in two visits is two entries, not one.
const countPairs = (byVisit: DeclinedByVisit): number =>
    Array.from(byVisit.values()).reduce((total, ids) => total + ids.size, 0);

export interface UseSmriqDeclinesResult {
    isDeclined: (visitId: string, itemId: string) => boolean;
    /** Refuses a decline that would push the pair count past the server max;
     *  a re-include (toggling an already-declined pair off) is always accepted. */
    toggleDecline: (visitId: string, itemId: string) => void;
    /** Whether this (visit, item) toggle can still be operated. Pair-keyed like the cap:
     *  the same item declined in another visit does not make this one operable. */
    isDeclineAllowed: (visitId: string, itemId: string) => boolean;
    atExclusionLimit: boolean;
    /** Replaces local state from the declinable `declined_items[]` per visit, which always
     *  wins over whatever was toggled locally. */
    reconcile: (services: readonly DeclinedItemsByVisit[]) => void;
    /** Flattened, capped {service_id: visitId, operation_id: itemId} pairs for the next
     *  `generate`'s `recommended_operation_exclusions`. */
    exclusions: SmriqOperationExclusion[];
}

/** Decline state lives here, outside `ConfiguredService` — never persisted, never
 *  sent anywhere but the next `generate` call. */
export function useSmriqDeclines(): UseSmriqDeclinesResult {
    const [declinedByVisit, setDeclinedByVisit] = useState<DeclinedByVisit>(() => new Map());

    const atExclusionLimit = countPairs(declinedByVisit) >= MAX_RECOMMENDED_OPERATION_EXCLUSIONS;

    const toggleDecline = useCallback((visitId: string, itemId: string) => {
        setDeclinedByVisit((prev) => {
            const current = new Set(prev.get(visitId));
            if (current.has(itemId)) {
                current.delete(itemId);
                return new Map(prev).set(visitId, current);
            }

            if (countPairs(prev) >= MAX_RECOMMENDED_OPERATION_EXCLUSIONS) {
                return prev;
            }

            current.add(itemId);
            return new Map(prev).set(visitId, current);
        });
    }, []);

    const isDeclineAllowed = useCallback(
        (visitId: string, itemId: string) =>
            !atExclusionLimit || (declinedByVisit.get(visitId)?.has(itemId) ?? false),
        [atExclusionLimit, declinedByVisit],
    );

    const reconcile = useCallback((services: readonly DeclinedItemsByVisit[]) => {
        const next = new Map<string, ReadonlySet<string>>();
        for (const service of services) {
            // Not gated on `can_decline`: the vendor reports that false on an already-declined
            // op, yet it stays a user exclusion to re-send.
            const declinable = service.declined_items.filter(isReincludableDeclinedItem);
            if (declinable.length > 0) {
                next.set(service.id, new Set(declinable.map((item) => item.id)));
            }
        }
        setDeclinedByVisit(next);
    }, []);

    const isDeclined = useCallback(
        (visitId: string, itemId: string) => declinedByVisit.get(visitId)?.has(itemId) ?? false,
        [declinedByVisit],
    );

    // Backstop cap only: toggleDecline already refuses anything past the max, and
    // reconcile takes the server's view.
    const exclusions = useMemo<SmriqOperationExclusion[]>(
        () =>
            Array.from(declinedByVisit.entries())
                .flatMap(([visitId, itemIds]) =>
                    Array.from(itemIds, (itemId) => ({ service_id: visitId, operation_id: itemId })),
                )
                .slice(0, MAX_RECOMMENDED_OPERATION_EXCLUSIONS),
        [declinedByVisit],
    );

    return {
        isDeclined,
        toggleDecline,
        isDeclineAllowed,
        atExclusionLimit,
        reconcile,
        exclusions,
    };
}
